import React, { useState } from 'react';
import Text from './TextComponent';
import Button from './Button';

export type Experience = {
  company: string;
  startYear: string;
  endYear: string | 'Present';
  entries: {
    sentence: string;
    keywords: string[];
  }[];
};

type ExperienceInputProps = {
  onSubmit: (exp: Experience) => void;
};

const ExperienceInput: React.FC<ExperienceInputProps> = ({ onSubmit }) => {
  const [company, setCompany] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear] = useState('');
  const [present, setPresent] = useState(false);
  const [sentenceInput, setSentenceInput] = useState('');
  const [entries, setEntries] = useState<Experience['entries']>([]);

  const extractKeywordsFromBraces = (text: string): string[] => {
    const matches = text.match(/\{([^}]+)\}/g);
    return matches ? matches.map((m) => m.slice(1, -1)) : [];
  };

  const addSentence = () => {
    if (!sentenceInput.trim()) return;
    const cleaned = sentenceInput.trim();
    const extractedKeywords = extractKeywordsFromBraces(cleaned);
    setEntries((prev) => [...prev, { sentence: cleaned, keywords: extractedKeywords }]);
    setSentenceInput('');
  };

  const removeSentence = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const isValid =
    !!company &&
    !!startYear &&
    (present || !!endYear) &&
    entries.length > 0;

  const submitExperience = () => {
    if (!isValid) return;
    onSubmit({
      company,
      startYear,
      endYear: present ? 'Present' : endYear,
      entries,
    });

    // Reset
    setCompany('');
    setStartYear('');
    setEndYear('');
    setPresent(false);
    setSentenceInput('');
    setEntries([]);
  };

  return (
    <div
      style={{
        fontFamily: 'Montserrat',
        fontSize: '16px',
        lineHeight: '24px',
        maxWidth: '720px',
        width: '100%',
        maxHeight: '80vh',         // ✅ mobile-friendly height
        margin: '24px auto',
        border: '1px solid #e0e0e0',
        borderRadius: '16px',
        backgroundColor: '#fff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable body */}
      <div
        className="typer-scroll-container"
        style={{
          overflowY: 'auto',
          padding: '20px',
          flex: 1,
          borderRadius: '16px',
        }}
      >
        <Text text="Add Experience" fontSize="24px" fontWeight={600} style={{ marginBottom: '16px' }} />

        {/* Company */}
        <div style={{ marginBottom: '16px' }}>
          <Text text="Company" fontSize="14px" fontWeight={500} />
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g., Acme Corp"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginTop: '6px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
            }}
          />
        </div>

        {/* Dates */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px', minWidth: 140 }}>
            <Text text="Start Year" fontSize="14px" fontWeight={500} />
            <input
              type="text"
              value={startYear}
              onChange={(e) => setStartYear(e.target.value)}
              placeholder="e.g., 2021"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                marginTop: '6px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
              }}
            />
          </div>
          <div style={{ flex: '1 1 140px', minWidth: 140 }}>
            <Text text="End Year" fontSize="14px" fontWeight={500} />
            <input
              type="text"
              value={present ? '' : endYear}
              onChange={(e) => setEndYear(e.target.value)}
              disabled={present}
              placeholder={present ? '' : 'e.g., 2023'}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                marginTop: '6px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                backgroundColor: present ? '#f0f0f0' : '#fff',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
            <label style={{ fontSize: '14px', userSelect: 'none', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={present}
                onChange={() => setPresent(!present)}
                style={{ marginRight: 8 }}
              />
              Present
            </label>
          </div>
        </div>

        {/* Sentence Input */}
        <div style={{ marginBottom: '16px' }}>
          <Text text="Sentence (wrap keywords in {braces})" fontSize="14px" fontWeight={500} />
          <textarea
            value={sentenceInput}
            onChange={(e) => setSentenceInput(e.target.value)}
            rows={2}
            placeholder="e.g., Built {React} apps and optimized {TypeScript} tooling."
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginTop: '6px',
              resize: 'vertical',
              fontFamily: 'Montserrat',
              fontSize: '16px',
            }}
          />
          <div style={{ marginTop: '10px' }}>
            <Button
              label="Add Sentence"
              width="auto"
              height="40px"
              onClick={addSentence}
              // defaults: background #8b2cd7, color #fff
            />
          </div>
        </div>

        {/* Sentences as removable chips */}
        {entries.length > 0 && (
          <div>
            <Text text="Sentences" fontSize="14px" fontWeight={600} style={{ marginBottom: '8px' }} />
            <div
              style={{
                position: 'relative',
                zIndex: 15,
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                marginBottom: '20px',
                top: 0,
              }}
            >
              {entries.map((entry, i) => (
                <Button
                  key={`${entry.sentence}-${i}`}
                  label={entry.sentence}
                  onClick={() => removeSentence(i)} // ✅ click to remove
                  width="auto"
                  height="36px"
                  fontSize="14px"
                  fontWeight={500}
                  color="#222"
                  backgroundColor="#f5f5f5"
                  style={{
                    borderRadius: '20px',
                    padding: '8px 14px',
                    border: '1px solid #dcdcdc',
                    boxShadow: '2px 4px 8px rgba(0,0,0,0.08)',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button (sticky footer) */}
      <div style={{ padding: '12px 12px 16px' }}>
        <Button
          label="Submit Experience"
          onClick={submitExperience}
          disabled={!isValid}
          width="100%"
          height="56px"
          fontWeight={700}
          // background is your purple by default
        />
      </div>
    </div>
  );
};

export default ExperienceInput;
